/* eslint-disable no-control-regex */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Button,
  TextField,
  Container,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import "./styles.css";

// スタブ
// ローディング
import LoadingDialog from "../components/LoadingDialog/LoadingDialog";
import * as yup from "yup";
// カスタイマイズしたもの
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { FormValues } from "../interfaces/formValues";

// redux
import { useAppSelector, useAppDispatch } from "../app/hooks";
import { changeDisplay } from "../features/managementProcess/managementProcessSlice";
import {
  update,
  customerInfoState,
  selectCustomerInfo,
} from "../features/customerInfo/customerInfoSlice";
import { selectEstimationAmountList } from "../features/estimationAmountList/estimationAmountListSlice";
import { EstimationAmountState } from "../features/estimationAmount/estimationAmountSlice";
import { selectFailedFiles } from "../features/failedFiles/failedFilesSlice";
import { validateEmailField } from "../utils/validation";
import axiosConfig from "../axiosConfig";

// Type definition for initial form values
type InitialFormValues = {
  email: string;
  pic: string;
  picRuby: string;
  companyName: string;
  companyNameRuby: string;
  postalCode: string;
  address: string;
  address2: string;
  phoneNumber: string;
  mobilePhoneNumber: string;
  projectName: string;
  remarks: string;
  estimateNumber: string;
  orderId: string;
  drawingsId: string[];
};

// コンポーネントを定義
const InputCustomerInfo: React.FC = () => {
  const dispatch = useAppDispatch();
  const customerInfo: customerInfoState = useAppSelector(selectCustomerInfo);
  const accountInfo = useAppSelector((state) => state.accountInfo);
  const estimateAmountList: EstimationAmountState[] = useAppSelector(
    selectEstimationAmountList
  );
  const failedFiles = useAppSelector(selectFailedFiles);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingLatestOrder, setIsLoadingLatestOrder] =
    useState<boolean>(false);

  useEffect(() => {
    let isHasData = false;
    estimateAmountList.forEach((item) => {
      if (item.status === "uploaded") isHasData = true;
    });
    if (!isHasData && failedFiles.length === 0) {
      navigate("/");
    }
    const fetchData = async () => {
      try {
        // const data = (await getDisableDays()).disableDaysArray;
        // setDisabledDates(data);
      } catch (error) {
        console.error("Error fetching disabled dates:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    dispatch(changeDisplay("InputCustomerInfo"));
  }, []);

  // エラーメッセージの定義
  const CustomErrorMessage: React.FC<{ name: keyof FormValues }> = ({
    name,
  }) => {
    const { values, errors } = useFormikContext<FormValues>();

    let message = "";
    let style = {};

    const optionalFields: (keyof FormValues)[] = ["remarks", "phoneNumber", "mobilePhoneNumber"];

    if (name === "remarks") {
      if (errors[name]) {
        message = errors[name] as string;
        style = { backgroundColor: "#FFFACD", color: "black" };
      } else {
        message =
          "任意の文字列を入力してください。キャンペーンコードをお持ちのお客様はキャンペーンコードを入力してください。空欄OK";
        style = { backgroundColor: "#98FB98", color: "black" };
      }
      return (
        <div className="customErrorMessage" style={style}>
          {message}
        </div>
      );
    }

    if (name === "phoneNumber" || name === "mobilePhoneNumber") {
      if (errors[name]) {
        message = errors[name] as string;
        style = { backgroundColor: "#FFFACD", color: "black" };
      } else if (!values[name] && !values.phoneNumber && !values.mobilePhoneNumber) {
        message = "電話番号または携帯電話番号のいずれかは必須です。";
        style = { backgroundColor: "#FFFACD", color: "black" };
      } else if (!values[name]) {
        message = "任意";
        style = { backgroundColor: "#98FB98", color: "black" };
      } else {
        message = "OK";
        style = { backgroundColor: "#98FB98", color: "black" };
      }
      return (
        <div className="customErrorMessage" style={style}>
          {message}
        </div>
      );
    }

    if (errors[name]) {
      // エラー表示は黄色
      message = errors[name] as string;
      style = { backgroundColor: "#FFFACD", color: "black" };
    } else if (!values[name]) {
      // 初回表示は黄色
      message = "必須項目です。";
      style = { backgroundColor: "#FFFACD", color: "black" };
    } else {
      // 問題ないときは緑色
      message = "OK";
      style = { backgroundColor: "#98FB98", color: "black" };
    }

    return (
      <div className="customErrorMessage" style={style}>
        {message}
      </div>
    );
  };

  // フォームの初期値
  const initialValues: InitialFormValues = {
    email:
      customerInfo.email !== "" ? customerInfo.email : accountInfo.email ?? "",
    pic:
      customerInfo.pic !== "" ? customerInfo.pic : accountInfo.fullname ?? "",
    picRuby:
      customerInfo.picRuby !== ""
        ? customerInfo.picRuby
        : accountInfo.fullname_kana ?? "",
    companyName:
      customerInfo.companyName !== "" ? customerInfo.companyName : "",
    companyNameRuby:
      customerInfo.companyNameRuby !== "" ? customerInfo.companyNameRuby : "",
    postalCode: customerInfo.postalCode !== "" ? customerInfo.postalCode : "",
    address:
      customerInfo.address !== ""
        ? customerInfo.address
        : accountInfo.address ?? "",
    address2: customerInfo.address2 !== "" ? customerInfo.address2 : "",
    phoneNumber:
      customerInfo.phoneNumber !== ""
        ? customerInfo.phoneNumber
        : accountInfo.phone_number ?? "",
    mobilePhoneNumber:
      customerInfo.mobilePhoneNumber !== ""
        ? customerInfo.mobilePhoneNumber
        : "",
    projectName:
      customerInfo.projectName !== "" ? customerInfo.projectName : "",
    remarks: customerInfo.remarks !== "" ? customerInfo.remarks : "",
    estimateNumber:
      customerInfo.estimateNumber !== "" ? customerInfo.estimateNumber : "",
    orderId: customerInfo.orderId !== "" ? customerInfo.orderId : "",
    drawingsId:
      customerInfo.drawingsId.length > 0 ? customerInfo.drawingsId : [],
  };

  // フォームのバリデーションスキーマ
  const validationSchema = yup.object({
    email: yup
      .string()
      .required("必須項目です。")
      .test("email-format", function (value) {
        if (!value) return true; // Required check handles empty values

        const validation = validateEmailField(value);
        if (!validation.isValid) {
          return this.createError({ message: validation.message });
        }
        return true;
      }),
    pic: yup
      .string()
      .matches(
        /^(?:[\u3041-\u3096\u30A1-\u30FA\u30FC\u4E00-\u9FFF\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\s]|[A-Za-z0-9])+$/,
        "氏名の値が不正です。"
      )
      .matches(/^.{3,50}$/, "3～50文字以内で入力してください。")
      .required("必須項目です。"),
    picRuby: yup
      .string()
      .matches(
        /^[\u30A1-\u30FA\u30FC\s]+$/,
        "氏名（カナ）の値が不正です。"
      )
      .matches(/^.{3,50}$/, "3～50文字以内で入力してください。")
      .required("必須項目です。"),
    companyName: yup
      .string()
      .matches(
        /^(?:[\u3041-\u3096\u30A1-\u30FA\u30FC\u4E00-\u9FFF\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\s|[A-Za-z0-9]|[0-9]|[&',.\-・])+$/,
        "会社名の値が不正です。"
      )
      .required("必須項目です。"),
    companyNameRuby: yup
      .string()
      .matches(/^[\u30A1-\u30FA\u30FC\s]+$/, "会社名（カナ）の値が不正です。")
      .required("必須項目です。"),
    postalCode: yup
      .string()
      .matches(
        /^(?!.*['"/\\;])\d{3}-\d{4}$/,
        "郵便番号（ハイフンあり）の値が不正です。"
      )
      .required("必須項目です。"),
    address: yup
      .string()
      .matches(
        /^(?:[\u3041-\u3096\u30A1-\u30FA\u30FC\u4E00-\u9FFF\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\uFF0D\s\u3000]|[A-Za-z0-9\-])+$/,
        "住所：県市町村区の値が不正です。"
      )
      .required("必須項目です。"),
    address2: yup
      .string()
      .matches(
        /^(?:[\u3041-\u3096\u30A1-\u30FA\u30FC\u4E00-\u9FFF\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\uFF0D\s\u3000]|[A-Za-z0-9\-])+$/,
        "住所：マンション名等詳細の値が不正です。"
      )
      .required("必須項目です。"),
    phoneNumber: yup
      .string()
      .test(
        "includes-hyphen",
        "ハイフンが含まれていません。",
        (value) => !value || value.includes("-")
      )
      .matches(/^$|^[0-9]+(-[0-9]+)*$/, "電話番号の値が不正です。")
      .test(
        "at-least-one-phone",
        "電話番号または携帯電話番号のいずれかは必須です。",
        function (value) {
          const { mobilePhoneNumber } = this.parent;
          return !!value || !!mobilePhoneNumber;
        }
      ),
    mobilePhoneNumber: yup
      .string()
      .test(
        "includes-hyphen",
        "ハイフンが含まれていません。",
        (value) => !value || value.includes("-")
      )
      .matches(/^$|^[0-9]+(-[0-9]+)*$/, "携帯電話番号の値が不正です。")
      .test(
        "at-least-one-phone",
        "電話番号または携帯電話番号のいずれかは必須です。",
        function (value) {
          const { phoneNumber } = this.parent;
          return !!value || !!phoneNumber;
        }
      ),
    projectName: yup
      .string()
      .matches(/^[^'"/\\;]*$/, "案件名の値が不正です。")
      .required("必須項目です。"),
    remarks: yup.string().matches(/^[^'"/\\;]*$/, "記号は使用できません。"),
  });

  // Date型をstring型に変換してFormValuesをシリアライズする関数
  const serializeFormValues = (values: FormValues): customerInfoState => {
    return {
      ...values,
      status: "complete",
    };
  };

  // 確認画面へボタン押下時
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const onSubmit = (values: FormValues) => {
    try {
      // 日付フォーマットだけDate型なのでシリアライズ可能な形に変換
      const serialized = serializeFormValues(values);
      dispatch(update(serialized));
      navigate("/input-info-confirmation");
    } catch (error) {
      console.error("ストア更新でエラーが発生しました", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // フォームのスタイル定義
  const formStyle = {
    margin: "20px 10px 5px 0px",
  };

  // 確認画面へのボタン押下時に不正なフィールドの名前を表示するための辞書
  const fields = [
    { name: "email", label: "Email" },
    { name: "pic", label: "氏名" },
    { name: "picRuby", label: "氏名（カナ）" },
    { name: "companyName", label: "会社名" },
    { name: "companyNameRuby", label: "会社名（カナ）" },
    { name: "postalCode", label: "郵便番号（ハイフンあり）" },
    { name: "address", label: "住所：県市町村区" },
    { name: "address2", label: "住所：マンション名等詳細" },
    { name: "phoneNumber", label: "電話番号(ハイフンあり)" },
    { name: "mobilePhoneNumber", label: "携帯電話番号(ハイフンあり)" },
    { name: "projectName", label: "案件名" },
    { name: "remarks", label: "備考" },
  ];

  const getLatestOrder = async () => {
    try {
      setIsLoadingLatestOrder(true);

      const response = await axiosConfig.get("/orders/latest");

      return response.data?.body;
    } catch (error) {
      console.error("Error fetching latest order:", error);
      return null;
    } finally {
      setIsLoadingLatestOrder(false);
    }
  };

  // Auto-fill helper function với data từ latest order
  const getAutoFillDataFromLatestOrder = async (
    currentValues: InitialFormValues
  ): Promise<InitialFormValues> => {
    const resLatestOrder = await getLatestOrder();
    const { order } = resLatestOrder;
    if (!order) {
      return {
        ...currentValues,
        ...(accountInfo.email && { email: accountInfo.email }),
        ...(accountInfo.fullname && { pic: accountInfo.fullname }),
        ...(accountInfo.fullname_kana && {
          picRuby: accountInfo.fullname_kana,
        }),
        ...(accountInfo.address && { address: accountInfo.address }),
        ...(accountInfo.phone_number && {
          phoneNumber: accountInfo.phone_number,
        }),
      };
    }

    return {
      ...currentValues,
      ...(order.email && { email: order.email }),
      ...(order.pic && { pic: order.pic }),
      ...(order.pic_ruby && { picRuby: order.pic_ruby }),
      ...(order.company_name && {
        companyName: order.company_name,
      }),
      ...(order.company_name_ruby && {
        companyNameRuby: order.company_name_ruby,
      }),
      ...(order.postal_code && { postalCode: order.postal_code }),
      ...(order.address && { address: order.address }),
      ...(order.address2 && { address2: order.address2 }),
      ...(order.phone_number && {
        phoneNumber: order.phone_number,
      }),
      ...(order.mobile_phone_number && {
        mobilePhoneNumber: order.mobile_phone_number,
      }),
      ...(order.project_name && {
        projectName: order.project_name,
      }),
      ...(order.remarks && { remarks: order.remarks }),
      ...(order.estimation_number && {
        estimateNumber: order.estimation_number,
      }),
    };
  };

  // Handle auto-fill button click
  const handleAutoFillClick = async (
    values: InitialFormValues,
    setValues: (values: InitialFormValues) => void
  ) => {
    try {
      const autoFilledData = await getAutoFillDataFromLatestOrder(values);
      setValues(autoFilledData);
    } catch (error) {
      console.error("Error auto-filling form:", error);
      // Show error message to user if needed
    }
  };

  return (
    <>
      <LoadingDialog isOpen={isLoading} message={"読込中"} />
      <Header pageTitle="お客様情報入力" />
      <Container style={{ marginTop: "20px", marginBottom: "100px" }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            errors,
            touched,
            setFieldValue,
            values,
            validateForm,
            setValues,
          }) => (
            <Form>
              {/* お客様登録情報から全て入力ボタン */}
              {accountInfo && accountInfo.email && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "20px",
                    padding: "10px 0",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleAutoFillClick(values, setValues)}
                    disabled={isLoadingLatestOrder}
                    sx={{
                      fontWeight: "bold",
                      padding: "12px 24px",
                      borderRadius: "4px",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      minWidth: "200px",
                    }}
                  >
                    {isLoadingLatestOrder
                      ? "読込中..."
                      : "最新の見積もり情報から全て入力する"}
                  </Button>
                </Box>
              )}

              <Field
                name="email"
                id="email"
                as={TextField}
                label="Email"
                fullWidth
                style={formStyle}
                autoComplete="email"
              />
              <CustomErrorMessage name="email" />
              <Field
                type="text"
                name="pic"
                id="pic"
                as={TextField}
                label="氏名"
                fullWidth
                style={formStyle}
                autoComplete="name"
              />
              <CustomErrorMessage name="pic" />
              <Field
                type="text"
                name="picRuby"
                id="picRuby"
                as={TextField}
                label="氏名（カナ）"
                fullWidth
                style={formStyle}
                autoComplete="additional-name"
              />
              <CustomErrorMessage name="picRuby" />
              <Field
                type="text"
                name="companyName"
                id="companyName"
                as={TextField}
                label="会社名"
                fullWidth
                style={formStyle}
                autoComplete="organization"
              />
              <CustomErrorMessage name="companyName" />
              <Field
                type="text"
                name="companyNameRuby"
                id="companyNameRuby"
                as={TextField}
                label="会社名（カナ）"
                fullWidth
                style={formStyle}
                autoComplete="organization"
              />
              <CustomErrorMessage name="companyNameRuby" />
              <Field
                type="text"
                name="postalCode"
                id="postalCode"
                as={TextField}
                label="郵便番号（ハイフンあり）"
                fullWidth
                style={formStyle}
                autoComplete="postal-code"
              />
              <CustomErrorMessage name="postalCode" />
              <Field
                type="text"
                name="address"
                id="address"
                as={TextField}
                label="住所：県市町村区"
                fullWidth
                style={formStyle}
                autoComplete="address-level1"
              />
              <CustomErrorMessage name="address" />
              <Field
                type="text"
                name="address2"
                id="address2"
                as={TextField}
                label="住所：マンション名等詳細"
                fullWidth
                style={formStyle}
                autoComplete="address-line2"
              />
              <CustomErrorMessage name="address2" />
              <Field
                type="text"
                name="phoneNumber"
                id="phoneNumber"
                as={TextField}
                label="電話番号(ハイフンあり)"
                fullWidth
                style={formStyle}
                autoComplete="tel"
              />
              <CustomErrorMessage name="phoneNumber" />
              <Field
                type="text"
                name="mobilePhoneNumber"
                id="mobilePhoneNumber"
                as={TextField}
                label="携帯電話番号(ハイフンあり)"
                fullWidth
                style={formStyle}
                autoComplete="tel"
              />
              <CustomErrorMessage name="mobilePhoneNumber" />
              <Field
                type="text"
                name="projectName"
                id="projectName"
                as={TextField}
                label="案件名"
                fullWidth
                style={formStyle}
              />
              <CustomErrorMessage name="projectName" />
              <Field
                type="text"
                name="remarks"
                id="remarks"
                as={TextField}
                label="備考"
                fullWidth
                style={formStyle}
              />
              <CustomErrorMessage name="remarks" />
              <Box
                sx={{
                  margin: "20px 200px",
                }}
              >
                <Stack direction="row" spacing={2}>
                  <>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={async () => {
                        const errors = await validateForm();
                        if (Object.keys(errors).length > 0) {
                          setValidationErrors(
                            Object.entries(errors).map(([field, message]) => {
                              const fieldLabel =
                                fields.find((f) => f.name === field)?.label ||
                                field;
                              return `『${fieldLabel}』が正常に入力されていません: ${message}`;
                            })
                          );
                          setOpen(true);
                        } else {
                          onSubmit(values);
                        }
                      }}
                    >
                      確認画面へ
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        navigate("/estimate-delivery-plan");
                      }}
                    >
                      キャンセル
                    </Button>
                  </>
                </Stack>
              </Box>
            </Form>
          )}
        </Formik>
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>入力エラー</DialogTitle>
          <DialogContent>
            <DialogContentText>
              以下のフィールドにエラーがあります。修正してください。
            </DialogContentText>
            <ul>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              閉じる
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
};

export default InputCustomerInfo;
